<script lang="ts">
  // The share popup's contents (ShareActivityButton puts it in a modal or a bottom sheet): a
  // report designer for the activity, and a footer to share, download or copy the image.
  import { onMount } from 'svelte'
  import { Button } from '@/lib/components/ui/button'
  import { cn } from '@/lib/utils'
  import ReportDesigner from './ReportDesigner.svelte'
  import type { ReportConfig } from './types'

  export let path: string
  export let name: string | null = null
  export let startDate: string | null = null
  /** A sheet sits on a phone: Share leads, and the footer clears the home indicator. */
  export let sheet = false

  let designer: ReportDesigner
  let config: ReportConfig | null = null
  let exportBlocked: string | null = null
  let exporting: 'download' | 'share' | 'copy' | null = null
  let shareNeedsTap = false
  let copied = false
  let copiedTimer: ReturnType<typeof setTimeout>

  $: canShare = designer?.canShareFiles ?? false
  // Copying lets people paste the image into X, Facebook, Discord and the like. Hidden where the
  // browser has blocked clipboard writes (browsers that can't query the permission just try).
  let canCopy = typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write
  onMount(() => {
    navigator.permissions?.query({ name: 'clipboard-write' as PermissionName })
      .then((p) => { if (p.state === 'denied') canCopy = false })
      .catch(() => {})
  })
  $: busy = !config || !!exportBlocked || !!exporting

  async function copy() {
    if (await designer.copy()) {
      copied = true
      clearTimeout(copiedTimer)
      copiedTimer = setTimeout(() => (copied = false), 2500)
    }
  }

  const dateFormat = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  $: dateLabel = startDate ? dateFormat.format(new Date(startDate)) : null
</script>

<div class="flex min-h-0 flex-1 flex-col">
  <header class="border-b border-border px-5 pb-3 pt-4 pr-12">
    <h2 class="m-0 flex items-center gap-2 text-base font-semibold">
      <i class="fa-solid fa-share-nodes text-sm text-orange-500" aria-hidden="true" />
      Share activity
    </h2>
    <p class="m-0 mt-0.5 flex min-w-0 gap-1.5 text-sm text-muted-foreground">
      {#if name}<span class="truncate font-medium text-foreground">{name}</span>{/if}
      {#if name && dateLabel}<span aria-hidden="true">·</span>{/if}
      {#if dateLabel}<span class="flex-none">{dateLabel}</span>{/if}
    </p>
  </header>

  <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
    <ReportDesigner bind:this={designer} mode="share" {path} bind:config bind:exportBlocked bind:exporting bind:shareNeedsTap />
  </div>

  <footer class={cn('flex flex-col gap-2 border-t border-border bg-background px-5 pt-3', sheet ? 'pb-[max(0.75rem,env(safe-area-inset-bottom))]' : 'pb-4')}>
    {#if exportBlocked}
      <p class="m-0 text-xs text-muted-foreground">{exportBlocked}</p>
    {/if}
    <div class={cn('flex flex-row items-center gap-2', !sheet && 'justify-end')}>
      {#if !sheet}
        <p class="m-0 mr-auto hidden text-xs text-muted-foreground md:block">
          {canShare ? 'Share opens your device\'s share options.' : 'Download it, then post it on Instagram, Facebook, X or anywhere else.'}
        </p>
      {/if}
      {#if canCopy && !sheet}
        <Button variant="outline" size="sm" disabled={busy} on:click={copy} title="Copy the image to paste into a post or message">
          <i class={cn('fa-solid mr-2 text-xs', exporting === 'copy' ? 'fa-spinner-third fa-spin' : copied ? 'fa-check' : 'fa-copy')} aria-hidden="true" />
          {exporting === 'copy' ? 'Creating image...' : copied ? 'Copied' : 'Copy image'}
        </Button>
      {/if}
      <!-- On phones the buttons fill the row; Share, when there is one, takes the extra room. -->
      <Button variant={canShare ? 'outline' : 'default'} size={sheet ? 'default' : 'sm'} disabled={busy} on:click={() => designer.download()}
        class={cn(!canShare && 'bg-orange-500 hover:bg-orange-400', sheet && !canShare && 'flex-1')}>
        <i class={cn('fa-solid text-xs', exporting === 'download' ? 'fa-spinner-third fa-spin' : 'fa-download', !(sheet && canShare) && 'mr-2')} aria-hidden="true" />
        {#if sheet && canShare}
          <span class="sr-only">Download image</span>
        {:else}
          {exporting === 'download' ? 'Creating image...' : 'Download image'}
        {/if}
      </Button>
      {#if canShare}
        <Button size={sheet ? 'default' : 'sm'} disabled={busy} on:click={() => designer.share()} class={cn('bg-orange-500 hover:bg-orange-400', sheet && 'flex-1')}>
          <i class={cn('fa-solid mr-2 text-xs', exporting === 'share' ? 'fa-spinner-third fa-spin' : 'fa-share-nodes')} aria-hidden="true" />
          {exporting === 'share' ? 'Creating image...' : shareNeedsTap ? 'Image ready, tap to share' : 'Share'}
        </Button>
      {/if}
    </div>
  </footer>
</div>
